import { useState } from 'react';
import { useDeleteExpenseCategoryMutation } from '@/features/expense-category/expense-category.mutations';
import { useExpenseCategories } from '@/features/expense-category/expense-category.queries';
// import { useExpenseCategories } from '@/features/expense-category/expense-category.queries';
import { toCapitalize } from '@/utils/text-utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { RoomDateColumn } from '@/components/datatable/columns/rooms/rooms-columns';
import { DataTableRowActions } from '@/components/datatable/data-table-row-action';
import { DataTableSimple } from '@/components/datatable/data-table-simple/data-table-simple';
import AlertModal from '@/components/modals/alert-modal';
import CreateUpdateExpenseCategoryModal from '@/components/modals/create-update-expense-category-modal';

// import CreateUpdateExpenseCategoryModal from '@/components/modals/create-update-expense-category-modal';

const expenseCategoryRouteSchema = z.object({
  mode: z.enum(['create', 'update']).optional(),
  expenseCategory: z.number().optional(),
});

export const Route = createFileRoute('/_app/(account)/account/settings')({
  validateSearch: zodSearchValidator(expenseCategoryRouteSchema),
  component: ExpenseCategories,
});

function ExpenseCategories() {
  return (
    <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
      <ExpenseCategoriesTabHeader />
      <ExpenseCategoriesTable />
    </div>
  );
}

function ExpenseCategoriesTabHeader() {
  const navigate = useNavigate();
  const { t } = useTranslation(['expense-category', 'common']);

  const createExpenseCategory = () => {
    navigate({ to: '.', search: { mode: 'create' } });
  };

  return (
    <div className='flex flex-row justify-between py-4'>
      <div className='flex flex-col'>
        <span className='text-xl font-bold'>{t('page_title', { format: 'uppercase' })}</span>
      </div>
      <div className='flex flex-row space-x-4'>
        <Button className='flex items-center gap-3' onClick={createExpenseCategory} size='default'>
          <Plus strokeWidth={4} className='h-6 w-6' />
          {toCapitalize(t('expense_category', { ns: 'common' }))}
        </Button>
      </div>
      <CreateUpdateExpenseCategoryModal />
    </div>
  );
}

function ExpenseCategoriesTable() {
  const { t } = useTranslation(['table', 'common']);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedExpenseCategory, setSelectedExpenseCategory] = useState({
    id: 0,
    name: '',
  });

  const navigate = useNavigate();
  const { data = [], isLoading } = useExpenseCategories();
  const { mutateAsync: deleteExpenseCategoryAsync } = useDeleteExpenseCategoryMutation();

  const updateExpenseCategory = (id: number) => {
    navigate({ to: '.', search: { mode: 'update', expenseCategory: id } });
  };

  const onSuccessDelete = async () => {
    if (!selectedExpenseCategory.id) return;

    await deleteExpenseCategoryAsync({ id: selectedExpenseCategory.id });
    setIsDeleteModalOpen(false);
    setSelectedExpenseCategory({ id: 0, name: '' });
  };

  const deleteExpenseCategory = (id: number, name: string) => {
    setSelectedExpenseCategory({ id, name });
    setIsDeleteModalOpen(true);
  };

  return (
    <>
      <DataTableSimple
        data={data}
        columns={['id', 'name', 'description', 'createdAt', 'updatedAt']}
        isLoading={isLoading}
        columnsLabels={{
          id: t('serial_id'),
          name: toCapitalize(t('name')),
          description: toCapitalize(t('description')),
          createdAt: t('created_at'),
          updatedAt: t('updated_at'),
        }}
        customRender={{
          createdAt: (row) => <RoomDateColumn date={row.getValue('createdAt')} />,
          updatedAt: (row) => <RoomDateColumn date={row.getValue('updatedAt')} />,
        }}
        extraColumns={[
          {
            id: 'actions',
            size: 50,
            cell: ({ row }) => (
              <DataTableRowActions
                row={row}
                menuItems={[
                  {
                    label: toCapitalize(t('edit', { ns: 'common' })),
                    onClick: () => updateExpenseCategory(row.getValue('id')),
                    isLoading: false,
                  },
                  {
                    label: toCapitalize(t('delete', { ns: 'common' })),
                    onClick: () => deleteExpenseCategory(row.getValue('id'), row.getValue('name')),
                    isLoading: false,
                  },
                ]}
              />
            ),
          },
        ]}
        className='flex h-full flex-col overflow-y-auto'
      />
      <AlertModal
        title='Διαγραφή Κατηγορίας'
        description={`Είσαι σίγουρος ότι θέλεις να διαγράψεις την κατηγορία "${selectedExpenseCategory.name}";`}
        setOpen={setIsDeleteModalOpen}
        isOpen={isDeleteModalOpen}
        onSuccess={onSuccessDelete}
      />
    </>
  );
}
